import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { filter, map, switchMap, tap } from 'rxjs/operators';
import { PaginationParams, QueryParams } from '@ml/shared/types/query-params';
import { PaginationResponse } from '@ml/shared/types/pagination-response';

export abstract class CrudApiService<T extends { id?: number }> {
  protected values = new BehaviorSubject<T[]>([]);
  protected value = new BehaviorSubject<T>(null);

  protected queryParams = new BehaviorSubject<QueryParams>(
    this.getInitialQueryParams()
  );
  protected loading = new BehaviorSubject<boolean>(null);
  protected count = new BehaviorSubject<number>(null);
  protected hasNext = new BehaviorSubject<boolean>(null);
  protected hasPrevious = new BehaviorSubject<boolean>(null);
  protected loadMore = false;
  protected cursor = new BehaviorSubject<string>(null);
  protected previousCursor = new BehaviorSubject<string>(null);

  protected constructor(protected _http: HttpClient, protected cache = false) {}

  abstract get url(): string;

  refresh() {
    this.values = new BehaviorSubject<T[]>(null);
    this.queryParams = new BehaviorSubject<QueryParams>(
      this.getInitialQueryParams()
    );
    this.loading = new BehaviorSubject<boolean>(null);
    this.count = new BehaviorSubject<number>(null);
  }

  search(search: string) {
    const queryParams: QueryParams = {
      ...this.queryParams.value
    };

    if (search) {
      queryParams.search = search;
    } else {
      delete queryParams.search;
    }

    this.queryParams.next(queryParams);
  }

  pageChange(pageEvent: PaginationParams) {
    const currentPage = this.queryParamsValue.page;

    if (currentPage > pageEvent.page) {
      this.loadPreviousPage();
    } else if (currentPage < pageEvent.page) {
      this.loadNextPage();
    } else {
      // changed only the size
      const queryParams: QueryParams = {
        ...this.queryParams.value,
        pageSize: pageEvent.pageSize
      };
      this.queryParams.next(queryParams);
    }
  }

  loadNextPage() {
    if (!this.hasNext.value) {
      return;
    }
    const queryParams: QueryParams = {
      ...this.queryParamsValue
    };

    if (this.cursor.value) {
      queryParams.cursor = this.cursor.value;
      delete queryParams.page;
    } else {
      delete queryParams.cursor;
      queryParams.page = this.queryParamsValue.page + 1;
    }

    this.loadMore = true;
    this.queryParams.next(queryParams);
  }

  loadPreviousPage() {
    if (!this.hasPrevious.value) {
      return;
    }
    const queryParams: QueryParams = {
      ...this.queryParamsValue
    };

    if (this.previousCursor.value) {
      queryParams.previousCursor = this.previousCursor.value;
      delete queryParams.page;
    } else {
      delete queryParams.previousCursor;
      queryParams.page = this.queryParamsValue.page - 1;
    }
    this.loadMore = true;
    this.queryParams.next(queryParams);
  }

  changeParams(queryParams: QueryParams) {
    this.queryParams.next({ ...queryParams });
  }

  subscribeValues$(): Observable<T[]> {
    return this.queryParams.pipe(
      filter(value => !!value && this.filterSubscribe()),
      tap(() => this.loading.next(true)),
      switchMap(params => this.list(params)),
      tap((response: PaginationResponse<T>) => this.count.next(response.count)),
      tap((response: PaginationResponse<T>) => {
        this.hasNext.next(!!response.next);
        this.hasPrevious.next(!!response.previous);
      }),
      tap((response: PaginationResponse<T>) => this.setCursor(response)),
      map((response: PaginationResponse<T>) => response.results),
      switchMap(results => {
        this.setValues(results);
        this.loadMore = false;
        return this.values.asObservable();
      }),
      tap(() => this.loading.next(false))
    );
  }

  subscribeValue$(valueId: number): Observable<T> {
    return this.get(valueId).pipe(
      switchMap(value => {
        this.value.next(value);
        return this.value.asObservable();
      })
    );
  }

  list(queryParams: QueryParams): Observable<PaginationResponse<T>> {
    const params = this.getParams(queryParams);
    return this._http.get<PaginationResponse<T>>(`${this.url}`, { params });
  }

  get(valueId: number): Observable<T> {
    return this._http.get<T>(`${this.url}/${valueId}`);
  }

  add(value: T): Observable<T> {
    return this._http
      .post<T>(`${this.url}`, value)
      .pipe(tap(result => this.addValue(result)));
  }

  edit(value: T): Observable<T> {
    return this._http
      .patch<T>(`${this.url}/${value.id}`, value)
      .pipe(tap(result => this.editValue(result)));
  }

  delete(id: number) {
    return this._http.delete(this.deleteUrl(id)).pipe(
      tap(() => this.deleteValue(id)),
      tap(() => this.getNextPageAfterDelete())
    );
  }

  get loading$() {
    return this.loading.asObservable();
  }

  get count$() {
    return this.count.asObservable();
  }

  get hasNext$() {
    return this.hasNext.asObservable();
  }

  get queryParams$() {
    return this.queryParams.asObservable();
  }

  get queryParamsValue() {
    return this.queryParams.getValue();
  }

  get currentValue(): T {
    return this.value.value;
  }

  protected deleteUrl(id: number) {
    return `${this.url}/${id}`;
  }

  /**
   * Method used for subclasses to stop the observable chain
   * For example if the route is a nested one and we need to wait for a
   * specific resource
   */
  protected filterSubscribe(): boolean {
    return true;
  }

  protected setValues(values: T[]) {
    if (!(this.cache && this.loadMore)) {
      this.values.next(values);
    } else {
      this.values.next([...this.values.value, ...values]);
    }
  }

  protected addValue(value: T) {
    if (!this.useSubscribe) {
      return;
    }
    this.values.next([value, ...this.values.value]);
  }

  protected editValue(value: T) {
    if (this.value.value && value.id === this.value.value.id) {
      return this.value.next(value);
    }

    if (!this.useSubscribe) {
      return;
    }

    const index = this.values.value.findIndex(item => item.id === value.id);
    this.values.value[index] = value;

    this.values.next([...this.values.value]);
  }

  /**
   * Get next page after delete
   * This method is used only if the cursor pagination is used
   */
  protected getNextPageAfterDelete() {
    if (!this.useSubscribe || !this.hasNext.value || !this.cursor.value) {
      return;
    }
    if (this.values.value.length < this.queryParamsValue.pageSize) {
      this.loadNextPage();
    }
  }

  protected deleteValue(valueId: number) {
    if (!this.useSubscribe) {
      return;
    }

    const index = this.values.value.findIndex(item => item.id === valueId);
    this.values.value.splice(index, 1);
    this.values.next([...this.values.value]);
  }

  getParams(queryParams: QueryParams) {
    const pageSize = queryParams ? queryParams.pageSize.toString() : '10';
    const params: any = {
      ...queryParams,
      pageSize
    };
    if (queryParams.page) {
      params.page = queryParams.page.toString();
    } else {
      delete params.page;
    }
    if (queryParams.cursor) {
      params.cursor = queryParams.cursor;
    }
    return params;
  }

  protected getInitialQueryParams() {
    return new QueryParams();
  }

  protected get useSubscribe(): boolean {
    return !!this.values.value;
  }

  private setCursor(response: PaginationResponse<T>) {
    const cursor = this.getCursorFromURL(response.next as string);
    const previousCursor = this.getCursorFromURL(response.previous as string);
    this.cursor.next(cursor);
    this.previousCursor.next(previousCursor);
  }

  private getCursorFromURL(url: string) {
    const name = 'cursor';
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(url);
    return results === null
      ? null
      : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }
}
